import React, { useState, useRef, useEffect } from 'react'
import {
    Typography,
    IconButton,
    Button,
    TextField,
    CircularProgress,
    Chip,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import SendIcon from '@material-ui/icons/Send'
import { Container } from '../../Container'
import { useAdminChat } from '../../../hooks/useAdminChat'
import { DisplayChatMessage } from './liveTypes'
import { formatMessageTimestamp } from '../../../helpers/date'

interface Props {
    conversationId: number
    conversationLabel: string
    onBack: () => void
}

const MessageBubble: React.FC<{
    msg: DisplayChatMessage
    primaryColor: string
    primaryContrast: string
    grey100: string
    textPrimary: string
    borderRadius: number
}> = ({
    msg,
    primaryColor,
    primaryContrast,
    grey100,
    textPrimary,
    borderRadius,
}) => {
    const isUser = msg.sender === 'user'
    const isAdmin = msg.sender === 'admin'
    const timestampLabel = msg.createdDate
        ? formatMessageTimestamp(msg.createdDate)
        : ''

    let backgroundColor: string
    let textColor: string
    if (isUser) {
        backgroundColor = primaryColor
        textColor = primaryContrast
    } else if (isAdmin) {
        backgroundColor = '#e8f5e9'
        textColor = '#1b5e20'
    } else {
        backgroundColor = grey100
        textColor = textPrimary
    }

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: isUser ? 'flex-end' : 'flex-start',
                marginBottom: 8,
            }}
        >
            <div style={{ maxWidth: '75%' }}>
                {(isAdmin || msg.sender === 'lori') && (
                    <Typography
                        variant="caption"
                        style={{
                            color: isAdmin ? '#2e7d32' : '#757575',
                            marginBottom: 2,
                            display: 'block',
                        }}
                    >
                        {isAdmin
                            ? 'Admin'
                            : msg.agentName
                            ? `Lori (${msg.agentName})`
                            : 'Lori'}
                    </Typography>
                )}
                <div
                    style={{
                        padding: '8px 12px',
                        borderRadius: borderRadius * 3,
                        backgroundColor,
                        color: textColor,
                        borderBottomRightRadius: isUser
                            ? borderRadius
                            : undefined,
                        borderBottomLeftRadius: !isUser
                            ? borderRadius
                            : undefined,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                    }}
                >
                    <Typography variant="body2" style={{ color: 'inherit' }}>
                        {msg.message}
                    </Typography>
                </div>
                {timestampLabel && (
                    <Typography
                        variant="caption"
                        style={{
                            color: '#757575',
                            marginTop: 4,
                            display: 'block',
                            textAlign: isUser ? 'right' : 'left',
                        }}
                    >
                        {timestampLabel}
                    </Typography>
                )}
            </div>
        </div>
    )
}

export const AdminChatView: React.FC<Props> = ({
    conversationId,
    conversationLabel,
    onBack,
}) => {
    const theme = useTheme()
    const [inputText, setInputText] = useState('')
    const messagesEndRef = useRef<HTMLDivElement>(null)

    const {
        messages,
        takeoverActive,
        takeoverAdminId,
        iAmTakeover,
        isConnected,
        isLoading,
        error,
        sendTakeover,
        sendRelease,
        sendMessage,
    } = useAdminChat(conversationId)

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [messages])

    const handleSend = () => {
        const trimmed = inputText.trim()
        if (!trimmed) return
        sendMessage(trimmed)
        setInputText('')
    }

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                width: '100%',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Container
                style={{
                    alignItems: 'center',
                    padding: theme.spacing(1),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    gap: theme.spacing(1),
                    flexShrink: 0,
                }}
            >
                <IconButton size="small" onClick={onBack}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6" noWrap style={{ flex: 1 }}>
                    {conversationLabel}
                </Typography>
                {isConnected && (
                    <Chip
                        size="small"
                        label="Connected"
                        style={{
                            backgroundColor: '#e8f5e9',
                            color: '#2e7d32',
                        }}
                    />
                )}
                {takeoverActive && !iAmTakeover && takeoverAdminId && (
                    <Chip
                        size="small"
                        label={`Taken over by admin #${takeoverAdminId}`}
                        style={{
                            backgroundColor: '#fff3e0',
                            color: '#e65100',
                        }}
                    />
                )}
                {iAmTakeover && (
                    <Chip
                        size="small"
                        label="You have takeover"
                        style={{
                            backgroundColor: '#e8f5e9',
                            color: '#1b5e20',
                        }}
                    />
                )}
            </Container>

            {/* Messages area */}
            <div
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    padding: theme.spacing(2),
                }}
            >
                {isLoading ? (
                    <Container
                        style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <CircularProgress size={32} />
                    </Container>
                ) : error ? (
                    <Container
                        style={{
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <Typography color="error">{error}</Typography>
                    </Container>
                ) : (
                    <>
                        {messages.map((msg, idx) => (
                            <MessageBubble
                                key={idx}
                                msg={msg}
                                primaryColor={theme.palette.primary.main}
                                primaryContrast={
                                    theme.palette.primary.contrastText
                                }
                                grey100={theme.palette.grey[100]}
                                textPrimary={theme.palette.text.primary}
                                borderRadius={theme.shape.borderRadius}
                            />
                        ))}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </div>

            {/* Bottom action area */}
            <Container
                style={{
                    flexDirection: 'column',
                    borderTop: `1px solid ${theme.palette.divider}`,
                    padding: theme.spacing(1),
                    gap: theme.spacing(1),
                    flexShrink: 0,
                }}
            >
                {!iAmTakeover && (
                    <Container style={{ justifyContent: 'center' }}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={sendTakeover}
                            disabled={
                                !isConnected || (takeoverActive && !iAmTakeover)
                            }
                        >
                            {takeoverActive
                                ? 'Another admin has takeover'
                                : 'Take Over Conversation'}
                        </Button>
                    </Container>
                )}

                {iAmTakeover && (
                    <>
                        <Container
                            style={{
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={sendRelease}
                                disabled={!isConnected}
                            >
                                Release to Lori
                            </Button>
                        </Container>
                        <Container
                            style={{
                                alignItems: 'flex-end',
                                gap: theme.spacing(1),
                            }}
                        >
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                placeholder="Type a message..."
                                value={inputText}
                                onChange={(e) => setInputText(e.target.value)}
                                onKeyDown={handleKeyDown}
                                multiline
                                rows={4}
                            />
                            <IconButton
                                color="primary"
                                onClick={handleSend}
                                disabled={!inputText.trim()}
                            >
                                <SendIcon />
                            </IconButton>
                        </Container>
                    </>
                )}

                {!iAmTakeover && !takeoverActive && (
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        align="center"
                        style={{ display: 'block' }}
                    >
                        You are observing this conversation. Click &quot;Take
                        Over&quot; to start sending messages.
                    </Typography>
                )}
            </Container>
        </div>
    )
}
